import { Pin, Clock, Building2, Landmark, Users, Calendar, User } from 'lucide-react';
import type { ComunicadoLeitura } from './ComunicadoLeituraSheet';
import {
  COMUNICADO_TIPO_COLORS,
  DESTINATARIO_LABEL_LONG,
} from './comunicados-shared';

const PERFIL_LABEL: Record<string, string> = {
  ADMIN: 'Administração',
  ESCOLA: 'Gestão da escola',
  MUNICIPAL: 'Prefeitura',
  PROFESSOR: 'Professor(a)',
};

function fmtData(d: string) {
  try {
    return new Date(d).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return d;
  }
}

function fmtHora(d: string) {
  try {
    return new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

interface Props {
  comunicado: ComunicadoLeitura | null;
  emptyMessage?: string;
  titleId?: string;
}

export function ComunicadoLeituraDetail({
  comunicado: c,
  emptyMessage = 'Selecione um comunicado na lista para ler a mensagem completa.',
  titleId,
}: Props) {
  if (!c) {
    return (
      <div className="com-desk-detail-empty">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  const tc = COMUNICADO_TIPO_COLORS[c.tipo] || { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' };
  const origem = c.municipio?.nome
    ? { icon: Landmark, label: `Prefeitura · ${c.municipio.nome}` }
    : c.escola?.nome
      ? { icon: Building2, label: c.escola.nome }
      : null;
  const publicadoPor = c.criadoPor?.nome
    ? `${c.criadoPor.nome}${c.criadoPor.perfil ? ` · ${PERFIL_LABEL[c.criadoPor.perfil] ?? c.criadoPor.perfil}` : ''}`
    : null;

  return (
    <article className="com-desk-detail-inner">
      <div className="com-desk-detail-head">
        <div className="com-desk-detail-badges">
          <span
            className="com-desk-badge-tipo"
            style={{ background: tc.bg, color: tc.color, borderColor: `${tc.color}33` }}
          >
            {c.tipo}
          </span>
          {c.fixado && (
            <span className="com-desk-badge-fixado">
              <Pin size={10} /> Fixado
            </span>
          )}
        </div>
        <h2 id={titleId} className="com-desk-detail-title">
          {c.titulo}
        </h2>
        <p className="com-desk-detail-date">
          <Clock size={12} />
          {fmtData(c.criadoEm)}
          {fmtHora(c.criadoEm) ? ` às ${fmtHora(c.criadoEm)}` : ''}
        </p>
      </div>

      <div className="com-desk-detail-meta">
        {origem && (
          <div className="com-desk-meta-item">
            <origem.icon size={12} />
            <span>{origem.label}</span>
          </div>
        )}
        <div className="com-desk-meta-item">
          <Users size={12} />
          <span>
            {DESTINATARIO_LABEL_LONG[c.destinatario] || c.destinatario}
            {c.destinatario === 'TURMA' && c.turma?.nome ? ` · ${c.turma.nome}` : ''}
          </span>
        </div>
        {publicadoPor && (
          <div className="com-desk-meta-item">
            <User size={12} />
            <span>Publicado por {publicadoPor}</span>
          </div>
        )}
        {c.dataEvento && (
          <div className="com-desk-meta-item">
            <Calendar size={12} />
            <span>Data do evento: {fmtData(c.dataEvento)}</span>
          </div>
        )}
      </div>

      {(() => {
        const anyC = c as any;
        const midiaUrl = anyC.configPreview?.midiaUrl || anyC.midiaUrl || anyC.imagemUrl || anyC.anexoUrl;
        if (!midiaUrl || typeof midiaUrl !== 'string') return null;
        return (
          <div style={{
            marginBottom: 16,
            borderRadius: 10,
            overflow: 'hidden',
            border: '1px solid var(--border-color, #e2e8f0)',
            background: '#0f172a',
            textAlign: 'center'
          }}>
            <img
              src={midiaUrl}
              alt={c.titulo}
              style={{ width: '100%', maxHeight: 380, objectFit: 'contain', display: 'block' }}
            />
          </div>
        );
      })()}

      <div className="com-desk-detail-corpo" style={{ borderLeftColor: tc.color }}>
        {c.corpo}
      </div>
    </article>
  );
}
